/**
 * The payroll link's setup as a draft (#297 ONB-23/24/27, X11; reference screen 10 «Fortsett når det
 * passer»): what the link should be used for and who finishes it, kept on the server per person and
 * company (PUT /settings/setup-draft), so it survives leaving, a reload and another browser. The draft is
 * never the link: the setup is done when the company is connected (the panel below), and the draft then
 * stays as history. «Lagre utkast» is the neutral save the leave question uses; pause keeps the choices
 * and stops the follow-up. A stale version keeps the person's own edits and shows what is saved now.
 */
import { errorText } from '@/api/client';
import {
  type DraftFields,
  type SetupDraft,
  staleDraft,
  useSetupDraftWrite,
  useSetupDrafts,
} from '@/api/onboarding';
import { useUsers } from '@/api/people';
import { T } from '@/i18n';
import { useDirty } from '@/shell/context';
import { useNeutralSave } from '@/shell/leave';
import { Button, Panel, toast } from '@/ui';
import { DateInput } from '@/ui/DateInput';
import { fmtD, fullName } from '@/ui/format';
import { useEffect, useState } from 'react';

const NAME = { tripletex: 'Tripletex', poweroffice: 'PowerOffice Go' } as const;
/** what the link can be used for in this app today (backend/services/setupDrafts.js INN, UT) */
const INN = [
  ['ansatte', 'Ansatte'],
  ['lonnsarter', 'Lønnsarter'],
  ['avdelinger', 'Avdelinger'],
  ['fravaer', 'Fravær og permisjoner'],
] as const;
const UT = [['timer', 'Godkjente timer til lønn']] as const;
const EMPTY: DraftFields = { inn: [], ut: [], ansvarlig: null };

interface Local {
  fields: DraftFields;
  remind: string;
}
const localOf = (d: SetupDraft | undefined): Local => ({
  // the link's draft only: the setup's import draft has other fields (#154)
  fields: d?.fields && 'inn' in d.fields ? d.fields : EMPTY,
  remind: d?.remind_on ?? '',
});
const same = (a: Local, b: Local) => JSON.stringify(a) === JSON.stringify(b);
const when = (iso: string) =>
  `${fmtD(iso.slice(0, 10))} ${new Date(iso).toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;

export function SetupDraftPanel() {
  const q = useSetupDrafts();
  const d = q.data?.find((x) => x.setup === 'lonnskobling');
  if (q.isError)
    return (
      <p className="hint" aria-live="polite" data-testid="draft-load-failed">
        {T('Utkastet til tilkoblingen kunne ikke leses. Valgene dine er ikke borte.')}{' '}
        <button type="button" className="mlink" onClick={() => q.refetch()}>
          {T('Prøv igjen')}
        </button>
      </p>
    );
  if (!d) return null;
  // keyed on the saved version: a fresh read replaces the form unless the person has edits of their own
  return <DraftForm draft={d} />;
}

function DraftForm({ draft }: { draft: SetupDraft }) {
  const write = useSetupDraftWrite();
  const users = useUsers();
  const [saved, setSaved] = useState(() => localOf(draft));
  const [local, setLocal] = useState(saved);
  const [version, setVersion] = useState(draft.version);
  // a newer draft saved elsewhere while this one had edits (ONB-27)
  const [newer, setNewer] = useState<SetupDraft | null>(null);
  const dirty = !same(local, saved);
  useDirty('setup-draft-lonnskobling', dirty, T('Utkast til tilkobling'));

  // the server's answer moved on (another tab, a refetch) and nothing is typed here: take it
  useEffect(() => {
    if (draft.version === version) return;
    if (!dirty) {
      const l = localOf(draft);
      setSaved(l);
      setLocal(l);
      setVersion(draft.version);
      setNewer(null);
    } else setNewer(draft);
  }, [draft, version, dirty]);

  async function send(extra: { paused?: boolean; discard?: true } = {}): Promise<boolean> {
    try {
      const r = await write.mutateAsync({
        setup: 'lonnskobling',
        version,
        ...(extra.discard ? { discard: true } : { fields: local.fields, remind_on: local.remind || null }),
        ...(extra.paused !== undefined ? { paused: extra.paused } : {}),
      });
      const l = localOf(r);
      setSaved(l);
      // the server's order of the choices, so a saved form is not taken for an edited one
      setLocal(l);
      setVersion(r.version);
      setNewer(null);
      return true;
    } catch (e) {
      const cur = staleDraft(e);
      if (cur) {
        setNewer(cur);
        return false;
      }
      toast(`${T('Utkastet ble ikke lagret')}: ${errorText(e)}`);
      return false;
    }
  }
  useNeutralSave('setup-draft-lonnskobling', dirty ? () => send() : null);

  // who may connect: the administrators of this company by membership (GET /users admin_here), the role the
  // connect routes and the server's check of this field read; users.role is legacy (CLAUDE.md domain traps)
  const admins = (users.data ?? []).filter((u) => u.active_status !== false && u.admin_here === true);
  const toggle = (k: 'inn' | 'ut', v: string, on: boolean) =>
    setLocal((l) => ({
      ...l,
      fields: { ...l.fields, [k]: on ? [...l.fields[k], v] : l.fields[k].filter((x) => x !== v) },
    }));
  const system = NAME[draft.system as keyof typeof NAME];
  const status =
    draft.status === 'off'
      ? T('Den direkte koblingen er ikke slått på ennå. Valgene i utkastet er beholdt.')
      : draft.status === 'done'
        ? T('Tilkoblingen er på. Utkastet er beholdt som historikk.')
        : draft.status === 'paused'
          ? T('Satt på pause {date}. Valgene er beholdt, og du får ingen påminnelse.', {
              date: draft.paused_at ? fmtD(draft.paused_at.slice(0, 10)) : '',
            })
          : draft.status === 'draft' && draft.saved_at
            ? draft.saved_by?.name
              ? T('Utkast lagret {when} av {name}. Det er ikke koblet til ennå.', {
                  when: when(draft.saved_at),
                  name: draft.saved_by.name,
                })
              : T('Utkast lagret {when}. Det er ikke koblet til ennå.', { when: when(draft.saved_at) })
            : T('Ikke påbegynt. Ingenting er lagret.');
  return (
    <Panel
      id="koblingsutkast"
      title={T('Plan for tilkoblingen til {system}', { system })}
      sub={T('Valgfritt. Lønnsfilen virker uten tilkobling. Lagre et utkast og fortsett når det passer.')}
    >
      <form
        className="sdraft"
        data-testid="setup-draft"
        onSubmit={(e) => {
          e.preventDefault();
          void send().then((ok) => ok && toast(T('Utkastet er lagret')));
        }}
      >
        <p className="sdraft-status" aria-live="polite" data-testid="draft-status" data-state={draft.status}>
          {status}
          {dirty && <b> {T('Du har endringer som ikke er lagret.')}</b>}
        </p>
        {newer && (
          <div className="sdraft-newer" role="alert" data-testid="draft-newer">
            <p>
              {newer.saved_by?.name
                ? T('Utkastet ble endret av {name} {when}. Endringene dine her er ikke lagret.', {
                    name: newer.saved_by.name,
                    when: newer.saved_at ? when(newer.saved_at) : '',
                  })
                : T('Utkastet ble endret et annet sted {when}. Endringene dine her er ikke lagret.', {
                    when: newer.saved_at ? when(newer.saved_at) : '',
                  })}
            </p>
            <span>
              <Button
                small
                onClick={() => {
                  setVersion(newer.version);
                  setNewer(null);
                }}
              >
                {T('Behold mine endringer')}
              </Button>
              <Button
                small
                onClick={() => {
                  const l = localOf(newer);
                  setSaved(l);
                  setLocal(l);
                  setVersion(newer.version);
                  setNewer(null);
                }}
              >
                {T('Bruk det lagrede')}
              </Button>
            </span>
          </div>
        )}
        <fieldset>
          <legend>{T('Hva skal hentes fra {system}?', { system })}</legend>
          {INN.map(([k, label]) => (
            <label key={k} className="sdraft-check">
              <input
                type="checkbox"
                checked={local.fields.inn.includes(k)}
                onChange={(e) => toggle('inn', k, e.target.checked)}
              />
              <span>{T(label)}</span>
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>{T('Hva skal sendes til {system}?', { system })}</legend>
          {UT.map(([k, label]) => (
            <label key={k} className="sdraft-check">
              <input
                type="checkbox"
                checked={local.fields.ut.includes(k)}
                onChange={(e) => toggle('ut', k, e.target.checked)}
              />
              <span>
                {T(label)}
                <small>{T('Av til du slår det på. Ingenting sendes før du trykker Send i Rapporter.')}</small>
              </span>
            </label>
          ))}
        </fieldset>
        <label className="sdraft-field">
          <span>{T('Hvem fullfører tilkoblingen?')}</span>
          <select
            className="inp"
            value={local.fields.ansvarlig ?? ''}
            onChange={(e) =>
              setLocal((l) => ({
                ...l,
                fields: { ...l.fields, ansvarlig: e.target.value ? Number(e.target.value) : null },
              }))
            }
          >
            <option value="">{T('Ikke valgt')}</option>
            {admins.map((u) => (
              <option key={u.id} value={u.id}>
                {fullName(u)}
              </option>
            ))}
          </select>
          <small>{T('Bare en administrator kan koble til.')}</small>
        </label>
        <label className="sdraft-field" htmlFor="sdraft-remind">
          <span>{T('Når vil du se på dette igjen?')}</span>
        </label>
        <DateInput
          className="inp"
          id="sdraft-remind"
          value={local.remind}
          onValueChange={(v) => setLocal((l) => ({ ...l, remind: v }))}
          aria-describedby="sdraft-remind-hint"
        />
        <small id="sdraft-remind-hint" className="hint">
          {T(
            'Valgfritt. Uten dato kommer ingen påminnelse. Med dato får den som fullfører tilkoblingen ett tips på e-post den dagen, eller hver administrator når ingen er valgt, bare hvis de har slått på tips under Kom i gang.',
          )}
        </small>
        <span className="sdraft-acts">
          <Button tone="pri" type="submit" disabled={write.isPending || !dirty} data-testid="draft-save">
            {T('Lagre utkast')}
          </Button>
          {draft.status === 'draft' && (
            <Button
              disabled={write.isPending}
              onClick={() =>
                void send({ paused: true }).then((ok) => ok && toast(T('Utkastet er satt på pause')))
              }
              data-testid="draft-pause"
            >
              {T('Sett på pause')}
            </Button>
          )}
          {draft.status === 'paused' && (
            <Button
              disabled={write.isPending}
              onClick={() =>
                void send({ paused: false }).then((ok) => ok && toast(T('Utkastet er tatt opp igjen')))
              }
              data-testid="draft-resume"
            >
              {T('Ta opp igjen')}
            </Button>
          )}
          {draft.version > 0 && (
            <button
              type="button"
              className="mlink"
              disabled={write.isPending}
              onClick={() =>
                void send({ discard: true }).then((ok) => ok && toast(T('Utkastet er forkastet')))
              }
              data-testid="draft-discard"
            >
              {T('Forkast utkastet')}
            </button>
          )}
        </span>
      </form>
    </Panel>
  );
}
